/**
 * THE REAL TAB ORDER — what the browser will actually stop on.
 *
 *   [tabindex="0"]                 ─┐
 *   <button> <input> <a href> …    ─┼─▶  in the tab order
 *   <summary> [contenteditable]    ─┘
 *
 *   [tabindex="-1"]  ─┐
 *   :disabled         ├─▶  NOT in the tab order
 *   aria-hidden/inert │
 *   display:none      ┘
 *
 * A natively focusable element carries NO tabindex attribute, so a
 * `[tabindex="0"]` query is structurally blind to every button, input, select
 * and anchor on the surface. Any gate written that way is green because it
 * cannot see failures.
 *
 * APG grid: "Only one of the focusable elements contained by the grid is
 * included in the page tab sequence."
 * https://www.w3.org/WAI/ARIA/apg/patterns/grid/
 */

/** Focusable with no tabindex attribute of their own. */
const NATIVE_TAB_STOPS = [
  'a[href]',
  'area[href]',
  'button',
  'input:not([type="hidden"])',
  'select',
  'textarea',
  'summary',
  'iframe',
  'object',
  'embed',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
].join(',');

const TAB_STOP_CANDIDATES = `${NATIVE_TAB_STOPS},[tabindex]`;

const isHiddenFromTabOrder = (node: Element): boolean => {
  if (node.closest('[aria-hidden="true"],[inert],[hidden]') !== null) {
    return true;
  }

  for (let ancestor: Element | null = node; ancestor !== null; ancestor = ancestor.parentElement) {
    const style = getComputedStyle(ancestor);

    if (style.display === 'none' || style.visibility === 'hidden') {
      return true;
    }
  }

  return false;
};

export const isTabStop = (node: Element): boolean => {
  const tabIndex = node.getAttribute('tabindex');

  if (tabIndex !== null && Number.parseInt(tabIndex, 10) < 0) {
    return false;
  }

  if (tabIndex === null && !node.matches(NATIVE_TAB_STOPS)) {
    return false;
  }

  return !node.matches(':disabled') && node.closest('fieldset[disabled]') === null && !isHiddenFromTabOrder(node);
};

/** Every element inside `root` the browser's Tab would stop on, in DOM order. */
export const tabStopsWithin = (root: ParentNode): HTMLElement[] =>
  [...root.querySelectorAll<HTMLElement>(TAB_STOP_CANDIDATES)].filter(isTabStop);

/**
 * Drops every tab stop inside `root` to `tabindex="-1"` and hands back the undo.
 * Programmatic `.focus()` still works; only the browser's Tab sequence changes.
 */
export const takeOutOfTabOrder = (root: ParentNode): (() => void) => {
  const restores = tabStopsWithin(root).map((node) => {
    const previous = node.getAttribute('tabindex');

    node.setAttribute('tabindex', '-1');

    return () => {
      if (previous === null) {
        node.removeAttribute('tabindex');
      } else {
        node.setAttribute('tabindex', previous);
      }
    };
  });

  return () => {
    for (const restore of restores) {
      restore();
    }
  };
};
